/**
 * ValueAtStakeView — Scenario comparison cards, EV bar chart,
 * and prioritised actions table for NRR improvement.
 */

import { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { DollarSign, TrendingUp, Info, Target } from "lucide-react";
import type { ValueAtStake } from "../types/diagnostic";

interface ValueAtStakeViewProps {
  valueAtStake: ValueAtStake;
  clientName: string;
}

/** Colour for each scenario index. */
const SCENARIO_COLORS = ["#6b7280", "#2563eb", "#16a34a"];
const SCENARIO_BG = ["bg-gray-50", "bg-blue-50", "bg-green-50"];
const SCENARIO_BORDER = ["border-gray-200", "border-blue-200", "border-green-200"];
const SCENARIO_ACCENT = ["text-gray-700", "text-blue-700", "text-green-700"];

/** Formats large numbers as $XM or $XB. */
function fmtMoney(m: number): string {
  if (Math.abs(m) >= 1000) return `$${(m / 1000).toFixed(1)}B`;
  return `$${Math.round(m)}M`;
}

export function ValueAtStakeView({ valueAtStake, clientName }: ValueAtStakeViewProps) {
  const [showMethodology, setShowMethodology] = useState(false);
  const { scenarios, topActions, base_arr_m, actionOverlapNote } = valueAtStake;

  /** Chart data for the EV gain comparison. */
  const evChartData = scenarios.map((s, i) => ({
    label: s.label,
    evGain: s.implied_ev_gain_m,
    color: SCENARIO_COLORS[i] ?? "#6b7280",
    nrr: s.nrr_pct,
  }));

  return (
    <div className="mck-card p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            Value at Stake — NRR Improvement Scenarios
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Translating NRR improvement to incremental ARR and enterprise value for {clientName} (base ARR: {fmtMoney(base_arr_m)})
          </p>
        </div>
        <button
          onClick={() => setShowMethodology(!showMethodology)}
          className="flex items-center gap-1 text-xs text-text-tertiary hover:text-mck-blue transition-colors"
        >
          <Info className="w-3.5 h-3.5" />
          Methodology
        </button>
      </div>

      {/* Methodology Note (expandable) */}
      {showMethodology && (
        <div className="bg-surface-secondary rounded-lg p-4 text-xs text-text-secondary leading-relaxed border border-surface-borderLight animate-fade-in">
          <p className="font-semibold text-text-primary mb-1">Methodology</p>
          <p>
            <strong>Incremental ARR</strong> = (Improved NRR - Current NRR) x Base ARR. Represents the annualised revenue gain from improving retention and expansion within the existing customer base.
          </p>
          <p className="mt-2">
            <strong>EV Multiple</strong> is scenario-specific. Current multiple reflects Okta&apos;s trading multiple. Improved and best-in-class multiples are based on the McKinsey NRR Maturity Benchmark finding that top-quartile NRR companies (NRR &ge; 121%) traded at 1.5-2.0x higher EV/NTM Revenue multiples on average (N=55 B2B SaaS companies, 2019-2022). The multiple expansion reflects the market&apos;s premium for durable, visible growth from the existing base.
          </p>
          <p className="mt-2">
            <strong>Implied EV Gain</strong> = Incremental ARR x Scenario Multiple + (Multiple Expansion x Current Revenue). This captures both the direct ARR contribution and the re-rating effect.
          </p>
        </div>
      )}

      {/* Scenario Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {scenarios.map((scenario, idx) => (
          <div
            key={scenario.label}
            className={`rounded-xl p-5 border ${SCENARIO_BG[idx]} ${SCENARIO_BORDER[idx]} ${
              idx === scenarios.length - 1 ? "ring-2 ring-green-300" : ""
            }`}
          >
            <p className={`text-xs font-semibold uppercase tracking-wide ${SCENARIO_ACCENT[idx]}`}>
              {scenario.label}
            </p>

            {/* NRR Badge */}
            <div className="mt-3 flex items-baseline gap-2">
              <span className={`text-3xl font-bold ${SCENARIO_ACCENT[idx]}`}>
                {scenario.nrr_pct}%
              </span>
              <span className="text-xs text-text-tertiary">NRR</span>
            </div>

            {/* Metrics */}
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5" /> Incremental ARR
                </span>
                <span className="font-semibold text-text-primary">
                  {scenario.incremental_arr_m === 0 ? "—" : `+${fmtMoney(scenario.incremental_arr_m)}/yr`}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary flex items-center gap-1">
                  <DollarSign className="w-3.5 h-3.5" /> EV Multiple
                </span>
                <span className="font-semibold text-text-primary">{scenario.ev_multiple}x</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary flex items-center gap-1">
                  <DollarSign className="w-3.5 h-3.5" /> Implied EV Gain
                </span>
                <span className={`font-bold ${idx > 0 ? SCENARIO_ACCENT[idx] : "text-text-primary"}`}>
                  {scenario.implied_ev_gain_m === 0 ? "—" : `+${fmtMoney(scenario.implied_ev_gain_m)}`}
                </span>
              </div>
            </div>

            {/* Multiple Rationale */}
            <p className="mt-3 text-[10px] text-text-tertiary leading-relaxed">
              {scenario.ev_multiple_rationale}
            </p>
          </div>
        ))}
      </div>

      {/* EV Gain Bar Chart */}
      <div>
        <h3 className="text-sm font-semibold text-text-primary mb-3">
          Enterprise Value Gain by Scenario
        </h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={evChartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#4a4a4a" }}
                tickLine={false}
                axisLine={{ stroke: "#e5e5e5" }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#6b7280" }}
                tickFormatter={(v: number) => fmtMoney(v)}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  if (!d) return null;
                  return (
                    <div className="bg-white border border-surface-borderLight rounded-lg shadow-mck p-3">
                      <p className="font-semibold text-sm">{d.label} (NRR: {d.nrr}%)</p>
                      <p className="text-xs text-text-secondary mt-1">
                        EV Gain: <strong>{fmtMoney(d.evGain)}</strong>
                      </p>
                    </div>
                  );
                }}
              />
              <Bar dataKey="evGain" radius={[4, 4, 0, 0]} maxBarSize={60}>
                {evChartData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} fillOpacity={0.85} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Actions */}
      <div className="border-t border-surface-borderLight pt-5">
        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-mck-blue" />
          Highest-Impact Actions
        </h3>
        {actionOverlapNote && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 leading-relaxed">
            <strong>Note on overlap:</strong> {actionOverlapNote}
          </p>
        )}
        <div className="space-y-3">
          {topActions.map((action, idx) => (
            <div
              key={action.id}
              className="flex items-start gap-3 bg-surface-secondary rounded-lg p-4 border border-surface-borderLight"
            >
              {/* Rank */}
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-mck-blue text-white text-xs font-bold flex items-center justify-center">
                {idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-sm font-medium text-text-primary">{action.title}</span>
                  <div className="flex items-center gap-3 text-xs flex-shrink-0">
                    <span className="text-green-700 font-semibold">+{action.nrr_impact_pct}pp NRR</span>
                    <span className="text-text-tertiary">{action.timeframe}</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">
                      {action.maturityDimension}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-text-tertiary mt-1 leading-relaxed">{action.narrative}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
